import React from "react";
import { Trans } from "@lingui/macro";
import { Tab, Tabs, TabList, TabPanel } from "react-tabs";
import { Button } from "@silversea-ssc/ui-components-react";
import { useLanguage } from "src/utils/localization/languages/I18nContext";
import getUrlsByLanguage from "src/urls/urls";
import * as styles from "./AllInclusiveBox.module.scss";
import { SectionContainer } from "@silversea-ssc/ui-components-react";
import InlineCTABox, {
  InlineCTABoxButton,
  InlineCTABoxContent
} from "components/InlineCTABox/InlineCTABox";
import { useGeolocation } from "../../../utils/localization/geolocation/GeolocationContext";
import { matchesAny } from "../../../utils/localization/geolocation";
import { DestinationPageQuery } from "types/graphql-types";
import PackageIcon from "components/PackageIcon/PackageIcon";
import classNames from "classnames/bind";
import TextBox from "components/TextBox/TextBox";
import { isPresent } from "src/utils/checks";

const cx = classNames.bind(styles);

const withLineBreaks = (value: string | undefined) =>
  value && (
    <ul
      className={styles.featureContentList}
      dangerouslySetInnerHTML={{
        __html: `<li>${value.replace("\n", "</li><li>")}</li>`
      }}
    />
  );

type AllInclusiveBoxProps = DestinationPageQuery["destination"] & {
  triggerSpecialTypeCruisesSearch: () => void;
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
const AllInclusiveBoxs: React.FC<AllInclusiveBoxProps> = (props) => {
  const geolocation = useGeolocation();
  const language = useLanguage();
  const urls = getUrlsByLanguage(language.code);
  const packages = props.packages?.filter(isPresent);

  return (
    <div
      id="all_inclusive"
      data-content-name="all_inclusive"
      className={styles.root}
    >
      <SectionContainer backgroundColor="transparent">
        <div className={styles.container}>
          <div className={styles.header}>
            <div className={styles.headerTitle}>
              {props.packagesTitle?.localized}
            </div>
            <div className={styles.headerContent}>
              <TextBox
                fontSize="medium"
                content={props.packagesHeaderContent?.localized}
              />
            </div>
          </div>
          <div className={styles.body}>
            {packages && (
              <Tabs className={styles.tabs}>
                <TabList className={styles.tabList}>
                  {packages.map((pkg, i) => (
                    <Tab
                      key={i}
                      className={styles.tab}
                      data-content-piece={pkg.name?.localized}
                    >
                      {pkg.name?.localized}
                    </Tab>
                  ))}
                </TabList>
                {packages.map((pkg, i) => {
                  const items = (pkg?.items || [])
                    .filter((item) => {
                      if (!item) return false;
                      return (
                        !item.filterExclusions?.length ||
                        !matchesAny(
                          item.filterExclusions,
                          geolocation.countryCode
                        )
                      );
                    })
                    .filter(
                      (item) =>
                        item &&
                        matchesAny(item.filters, geolocation.countryCode)
                    )
                    .filter(isPresent);
                  return (
                    <TabPanel
                      key={i}
                      className={styles.tabPanel}
                      selectedClassName={styles.tabPanelSelected}
                    >
                      <div className={styles.features}>
                        {items.map((item, j) => (
                          <div
                            key={j}
                            className={cx({
                              featureContent: true,
                              featureContentHighlighted: item.isHighlighted
                            })}
                          >
                            {item.icon && <PackageIcon icon={item.icon} />}
                            <div className={styles.featureContentTitle}>
                              {item.name?.localized}
                            </div>
                            <div className={styles.featureContentDescription}>
                              {item.description &&
                                withLineBreaks(item.description.localized)}
                            </div>
                          </div>
                        ))}
                      </div>
                      {pkg.showSpecialCruisesFilterLink &&
                        props.filterSpecialCruisesLinkText && (
                          <div className={styles.tabPanelFooter}>
                            <Button
                              renderAs="button"
                              variant="link"
                              onClick={props.triggerSpecialTypeCruisesSearch}
                              data-content-piece="bridge_cruises_anchor"
                            >
                              {props.filterSpecialCruisesLinkText.localized}
                            </Button>
                          </div>
                        )}
                    </TabPanel>
                  );
                })}
              </Tabs>
            )}
          </div>
          {props.allInclusiveDisclaimer?.localized && (
            <div className={styles.footerDisclaimer}>
              {props.allInclusiveDisclaimer?.localized}
            </div>
          )}

          <InlineCTABox className={styles.footer} align="center">
            <InlineCTABoxContent>
              <Trans>
                Contact our {props.name?.localized} destination experts and
                request your personalised quote
              </Trans>
            </InlineCTABoxContent>
            <InlineCTABoxButton>
              <Button
                variant="primary"
                centerText
                href={urls.serviceLinks.raq.href}
                data-content-piece="request_a_quote"
              >
                <Trans>Request a Quote</Trans>
              </Button>
            </InlineCTABoxButton>
          </InlineCTABox>
        </div>
      </SectionContainer>
    </div>
  );
};

export default AllInclusiveBoxs;
